import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Chip, Box, Button,
  Typography,
  Card, CardContent, CardHeader, Grid,
  FormControl, FormControlLabel, FormLabel, Radio, RadioGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useTranslation } from 'react-i18next';
import useStorageUpload from '../useStorageUpload';
import TagEditor from '../Widgets/TagEditor';
import { useUser } from '../WebSocketContext';

const ActionsCreateExcelFromTemplate = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { canWriteStorage } = useUser();
  const [targetPath, setTargetPath] = useState('');
  const [templatePath, setTemplatePath] = useState('');
  const [templateDisplayPath, setTemplateDisplayPath] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [replaceIfExists, setReplaceIfExists] = useState(false);
  const [templateBrowseDisabled, setTemplateBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const targetRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const normalizePath = useCallback((p) => (p || '').replace(/\\/g, '/'), []);

  const getDisplayPath = useCallback((absPath) => {
    if (!absPath) return '';
    if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
      const primaryRoot = normalizePath(allowedRoots[0]);
      const normalized = normalizePath(absPath);
      if (normalized === primaryRoot) return '/';
      if (normalized.startsWith(primaryRoot + '/')) {
        const rel = normalized.slice(primaryRoot.length);
        return rel ? `/${rel.replace(/^[\\/]+/, '')}` : '/';
      }
    }
    return absPath;
  }, [restrictToUserFolder, allowedRoots, normalizePath]);

  const resolveAbsolutePath = useCallback((incomingPath) => {
    if (!incomingPath) return '';

    const hasRestrict = restrictToUserFolder && allowedRoots && allowedRoots.length > 0;
    const primaryRoot = hasRestrict ? allowedRoots[0] : '';

    if (hasRestrict && typeof incomingPath === 'string') {
      const isRootRelative = incomingPath.startsWith('/') || incomingPath.startsWith('\\');
      const isDriveOrUNC = /^([A-Za-z]:[\\/]|\\\\)/.test(incomingPath);
      if (isRootRelative && !isDriveOrUNC) {
        const separator = primaryRoot.includes('\\') ? '\\' : '/';
        const trimmed = incomingPath.replace(/^[/\\]+/, '');
        return trimmed ? `${primaryRoot.replace(/[\\/]+$/, '')}${separator}${trimmed}` : primaryRoot;
      }
    }

    const pathIsAbsolute = /^([A-Za-z]:[\\/]|\\\\|\/)/.test(incomingPath);
    if (pathIsAbsolute || !hasRestrict) {
      return incomingPath;
    }

    const separator = primaryRoot.includes('\\') ? '\\' : '/';
    const trimmed = incomingPath.replace(/^[/\\]+/, '');
    return trimmed ? `${primaryRoot.replace(/[\\/]+$/, '')}${separator}${trimmed}` : primaryRoot;
  }, [restrictToUserFolder, allowedRoots]);

  const {
    uploading: templateUploadLoading,
    inputRef: uploadInputRef,
    handleFileChange: handleTemplateFileChange,
    triggerFileDialog: handleTemplateUploadClick
  } = useStorageUpload({
    caseid,
    accept: '.xlsx',
    httpEndpoint: '/api/v1/storage/template-files',
    successMessage: t('create_excel_template.upload_success', 'Template uploaded to storage'),
    errorMessage: t('create_excel_template.upload_error', 'Template upload failed'),
    sendingMessage: t('create_excel_template.uploading_template', 'Uploading template to storage...'),
    onSuccess: (data) => {
      if (data?.path) {
        const absPath = resolveAbsolutePath(data.path);
        setTemplatePath(absPath);
        setTemplateDisplayPath(getDisplayPath(absPath));
      }
    }
  });

  useEffect(() => {
    if (!templatePath) {
      if (templateDisplayPath) setTemplateDisplayPath('');
      return;
    }
    const absPath = resolveAbsolutePath(templatePath);
    if (absPath !== templatePath) {
      setTemplatePath(absPath);
      return;
    }
    const display = getDisplayPath(absPath);
    if (display !== templateDisplayPath) {
      setTemplateDisplayPath(display);
    }
  }, [templatePath, templateDisplayPath, resolveAbsolutePath, getDisplayPath]);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createexcelfromtemplate',
    onSaveStatusChange,
    customFields: {
      target: '/{filename}_template.xlsx',
      templatePath: '',
      selectedTags: [],
      replaceIfExists: false
    },
    customComparison: (initial) => {
      const initialTargetDisplay = initial.target ? getDisplayPath(initial.target) : '';
      const initialTemplateDisplay = initial.templatePath ? getDisplayPath(initial.templatePath) : '';
      return (
        initialTargetDisplay === targetPath &&
        initialTemplateDisplay === templateDisplayPath &&
        JSON.stringify(initial.selectedTags || []) === JSON.stringify(selectedTags) &&
        Boolean(initial.replaceIfExists) === replaceIfExists
      );
    },
    customSaveData: () => ({
      target: targetPath,
      templatePath: templateDisplayPath,
      selectedTags,
      replaceIfExists
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      if (parsedData.templatePath !== undefined) {
        const absPath = resolveAbsolutePath(parsedData.templatePath);
        setTemplatePath(absPath);
        setTemplateDisplayPath(getDisplayPath(absPath));
      }
      if (parsedData.selectedTags !== undefined) {
        setSelectedTags(parsedData.selectedTags);
      }
      if (parsedData.replaceIfExists !== undefined) {
        setReplaceIfExists(Boolean(parsedData.replaceIfExists));
      }
      if (parsedData.target) {
        setTargetPath(getDisplayPath(parsedData.target));
      }
    },
    enableConditions: true
  });

  const { handleBrowseDirectory: handleBrowseTemplateDirectory, FolderBrowserComponent: TemplateFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      const absPath = resolveAbsolutePath(selectedPath);
      setTemplatePath(absPath);
      setTemplateDisplayPath(getDisplayPath(absPath));
    },
    onFolderBrowserClose: () => {
      setTemplateBrowseDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (!folderPath) return;
      const absFolder = resolveAbsolutePath(folderPath);
      const displayFolder = getDisplayPath(absFolder);
      let folderPathClean = displayFolder.replace(/[\\/]+$/, '');
      const separator = folderPathClean.includes('\\') ? '\\' : '/';
      setTargetPath(`${folderPathClean}${separator}{filename}_template.xlsx`);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    }
  });

  const customDataTags = [{
    key: 'tag_ai_generated_content',
    label: 'AI Generated Content',
    value: 'ai_generated_content',
    description: 'Content generated by AI based on the prompt'
  }];

  const availableTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) return [];
    
    return tags.map((tag) => ({
      key: tag.key || tag.value || tag.variable || tag.label,
      label: tag.label || tag.key || tag.value || tag.variable,
      value: tag.value || tag.variable || tag.key || tag.label,
      variable: tag.variable || tag.value || tag.key || tag.label,
      description: tag.description || '',
      type: tag.type || 'text',
      reply: tag.reply || '',
      tag_type: tag.tag_type || ''
    }));
  }, [tags]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'main') {
      targetRef.current?.insertTag(value);
    } else if (menuType === 'tags') {
      if (!selectedTags.includes(value)) {
        setSelectedTags([...selectedTags, value]);
      }
    }
    handleMenuClose();
  };

  const handleTagRemove = useCallback((tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  }, [selectedTags]);

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.create_excel_template', 'Create Excel from Template')}
      isSaveDisabled={isSaveDisabled}
      onSave={() => baseHandleSave()}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags: availableTags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
        menuType: menuType,
        additionalTags: customDataTags
      }}
    >
      <input
        type="file"
        accept=".xlsx"
        ref={uploadInputRef}
        style={{ display: 'none' }}
        onChange={handleTemplateFileChange}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#1f7a5b',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(31, 122, 91, 0.2)',
                  }}>
                    TEMPLATE
                  </Box>
                  {t('workflow.template_path', 'Template Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1f7a5b',
                },
                '&:focus-within': {
                  borderColor: '#1f7a5b',
                  boxShadow: '0 0 0 3px rgba(31, 122, 91, 0.1)',
                },
              }}>
                <Box sx={{
                  padding: '12px 16px',
                  minHeight: '28px',
                  fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                }}>
                  <input
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                    }}
                    value={templateDisplayPath}
                    onChange={(e) => {
                      const displayValue = e.target.value;
                      setTemplateDisplayPath(displayValue);
                      setTemplatePath(resolveAbsolutePath(displayValue));
                    }}
                    placeholder="C:\\templates\\template.xlsx"
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTemplateBrowseDisabled(true);
                    handleBrowseTemplateDirectory();
                  }}
                  disabled={templateBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleTemplateUploadClick}
                  disabled={templateUploadLoading || !canWriteStorage}
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {templateUploadLoading ? t('create_excel_template.uploading', 'Uploading...') : t('create_excel_template.upload_template', 'Upload Template')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.template_path_help_excel', 'Path to the Excel template file with {tag} placeholders')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#1f7a5b',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(31, 122, 91, 0.2)',
                  }}>
                    OUTPUT
                  </Box>
                  {t('workflow.target_path', 'Target Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                    caseid={caseid}
                ref={targetRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('create_excel_template.target_path_placeholder', 'Enter target path with placeholders like {filename}_output.xlsx')}
                singleLine
                onTagClick={(e) => handleMenuOpen(e, 'main')}
                tags={availableTags}
              />

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mt: 1.5,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControl>
                  <FormLabel sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 0.5 }}>
                    {t('workflow.file_exists_behavior_label', 'If output file already exists')}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={replaceIfExists ? 'replace' : 'skip'}
                    onChange={(e) => setReplaceIfExists(e.target.value === 'replace')}
                  >
                    <FormControlLabel
                      value="replace"
                      control={<Radio size="small" />}
                      label={t('workflow.file_exists_behavior_replace', 'Replace')}
                    />
                    <FormControlLabel
                      value="skip"
                      control={<Radio size="small" />}
                      label={t('workflow.file_exists_behavior_skip', 'Skip')}
                    />
                  </RadioGroup>
                </FormControl>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                  {t('workflow.file_exists_behavior_help', 'Choose whether to overwrite the existing output file or skip this action.')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#1f7a5b',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(31, 122, 91, 0.2)',
                  }}>
                    TAGS
                  </Box>
                  {t('workflow.selected_tags', 'Selected Tags for Template')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={(e) => handleMenuOpen(e, 'tags')}
                  startIcon={<AddIcon />}
                  sx={{
                    color: '#1f7a5b',
                    borderColor: '#1f7a5b30',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    mb: 1,
                  }}
                >
                  {t('workflow.add_tags', 'Add Tags')}
                </Button>
                {selectedTags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedTags.map((tagValue, index) => {
                      let tagObj = availableTags.find(tag =>
                        tag.variable === tagValue || tag.value === tagValue
                      );

                      if (!tagObj && tags && Array.isArray(tags)) {
                        tagObj = tags.find(tag =>
                          tag.variable === tagValue || tag.value === tagValue || tag.key === tagValue
                        );
                      }

                      const displayLabel = tagObj ? (tagObj.label || tagObj.variable || tagObj.value || tagObj.key) : tagValue;

                      return (
                        <Chip
                          key={index}
                          label={displayLabel}
                          onDelete={() => handleTagRemove(tagValue)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.selected_tags_help_excel', 'These tags will be available as variables in your Excel template. Use them like {tag_name} in cells.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {TemplateFolderBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateExcelFromTemplate);
